import { useState } from 'react';
import { appendPlanningNotes } from '../../lib/orders';

interface PlanningNotesSectionProps {
  orderId: string;
  value: string;
  onAppended: (newValue: string) => void;
}

/** Boven dit aantal regels klapt de SF-historie standaard in (nieuwste onderaan). */
const COLLAPSED_LINES = 6;

export function PlanningNotesSection({ orderId, value, onAppended }: PlanningNotesSectionProps) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historieOpen, setHistorieOpen] = useState(false);

  // De tijdlijn leeft sinds actor-attributie fase 2 in het audit-log
  // (ActivityFeed); dit SF-tekstblok is legacy-parity tot de cut en blijft
  // leesbaar als historie. appendNote plakt onderaan, dus ingeklapt tonen we
  // de staart (de recente notities), niet de kop.
  const regels = value ? value.split('\n') : [];
  const ingeklapt = !historieOpen && regels.length > COLLAPSED_LINES;
  const zichtbaar = ingeklapt ? regels.slice(-COLLAPSED_LINES).join('\n') : value;

  const handleAppend = async () => {
    const entry = draft.trim();
    if (!entry || saving) return;
    setSaving(true);
    setError(null);
    try {
      const result = await appendPlanningNotes(orderId, entry, 'Office');
      onAppended(result.nieuweInhoud);
      setDraft('');
      setAdding(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Toevoegen mislukt');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xs uppercase tracking-wide text-slate-500 font-medium">
          Opmerkingen voor planning
        </h3>
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
          >
            + Notitie toevoegen
          </button>
        )}
      </div>
      {adding && (
        <div className="space-y-2 mt-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            autoFocus
            placeholder="Notitie toevoegen aan planning…"
            className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
          <div className="flex items-center justify-end gap-2">
            {error && <span className="text-xs text-red-600">{error}</span>}
            <button
              type="button"
              onClick={() => {
                setAdding(false);
                setDraft('');
                setError(null);
              }}
              disabled={saving}
              className="text-sm text-slate-500 hover:text-slate-700 px-2 py-1.5"
            >
              Annuleren
            </button>
            <button
              type="button"
              onClick={handleAppend}
              disabled={saving || !draft.trim()}
              className="text-sm bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded disabled:opacity-50"
            >
              {saving ? 'Opslaan…' : 'Toevoegen'}
            </button>
          </div>
        </div>
      )}
      {value ? (
        <>
          <pre className="mt-3 text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded p-3 whitespace-pre-wrap font-mono">
            {zichtbaar}
          </pre>
          {regels.length > COLLAPSED_LINES && (
            <button
              type="button"
              onClick={() => setHistorieOpen((v) => !v)}
              className="mt-1 text-xs text-indigo-600 hover:underline"
            >
              {historieOpen
                ? 'Historie inklappen'
                : `Volledige historie tonen (Salesforce, ${regels.length} regels)`}
            </button>
          )}
        </>
      ) : (
        !adding && (
          <p className="mt-1 text-xs text-slate-400">
            Geen opmerkingen — notities hier zijn zichtbaar voor de planning en chauffeurs.
          </p>
        )
      )}
    </div>
  );
}
